import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {variableChipSx} from "../../variables/variableChipStyles";
import {formatChoices, variableExample} from "../model/resolution";
import type {PickerVariable, VariableAttributes, VariablePreview} from "../model/types";
import {cleanFallback} from "../model/variableToken";
import {VariableField} from "../shared/VariableField";

interface Props {
    variable: PickerVariable | null;
    value: VariableAttributes;
    /** The host owns the document: the panel reports the edit and never writes it. */
    onChange: (attributes: Partial<VariableAttributes>) => void;
    /** What the previews resolve against; its language is required so examples are never silently German. */
    preview: VariablePreview;
}

export function VariableDetailPanel({variable, value, onChange, preview}: Props) {
    const {t} = useTranslation();

    if (!variable) {
        return (
            <Typography variant="body2" color="text.secondary">
                {t("Select a variable to see what it shows and give it a default.")}
            </Typography>
        );
    }

    const choices = formatChoices(variable, {...value, language: preview.language, context: preview.context});
    // A format the variable does not offer (a stale token, a changed kind) shows the first choice
    // instead of an empty select.
    const format = choices.some(choice => choice.value === value.format) ? value.format : choices[0]?.value;
    const Icon = variable.icon;

    return (
        <Stack gap={2.5}>
            <Stack gap={1}>
                <Stack direction="row" alignItems="center" gap={1}>
                    {!!Icon && (
                        <Box sx={{display: "inline-flex", color: "text.secondary"}}>
                            <Icon fontSize="small"/>
                        </Box>
                    )}
                    <Box component="span" sx={variableChipSx}>{variable.key}</Box>
                </Stack>
                <Typography variant="body2">{variable.label}</Typography>
            </Stack>

            {/* No empty line for a catalogue without descriptions, as in the picker's info card. */}
            {!!variable.description && (
                <Typography variant="body2" color="text.secondary">{variable.description}</Typography>
            )}

            <Stack gap={0.5}>
                {/* The chip's own fallback, so a cleared Default shows as empty rather than "-". */}
                <VariableField label={t("Shows as")}>
                    {variableExample(variable, preview, {format: value.format, fallback: value.fallback})}
                </VariableField>
                <VariableField label={t("Source")}>
                    {variable.source ?? variable.path ?? variable.compute ?? t("Derived")}
                </VariableField>
                <VariableField label={t("Scope")}>{variable.scope}</VariableField>
            </Stack>

            <Divider/>

            <Stack gap={1}>
                <TextField
                    label={t("Default")}
                    size="small"
                    fullWidth
                    value={value.fallback}
                    placeholder={t("Nothing")}
                    // Braces would end the token early; the chip would not survive its own text.
                    onChange={event => onChange({fallback: cleanFallback(event.target.value)})}
                />
                <Typography variant="body2" color="text.secondary">
                    {t("Used when no value resolves for this variable.")}
                </Typography>
            </Stack>

            {choices.length > 0 && (
                <TextField
                    select
                    label={t("Format")}
                    size="small"
                    fullWidth
                    value={format}
                    onChange={event => onChange({format: event.target.value})}
                >
                    {choices.map(choice => (
                        <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
                    ))}
                </TextField>
            )}
        </Stack>
    );
}

export default VariableDetailPanel;
